import React, { memo, useState, useEffect } from "react";
import styles from "./createByBatch.module.scss";
import CustomDialogUploadFile from "../../../shared/components/CustomDialogUploadFile";
import SVG from "react-inlinesvg";
import { useTranslation } from "react-i18next";
import { api } from "../../../app/conf";
import { downloadSVG } from "../../../shared/helpers/icons";
import WarehouseAfterBatchModal from "./WarehouseAfterBatchModal";

const CreateByBatch = ({
    open,
    onClose,
    submitFetching,
    submitValidateBatchFetching,
    submitValidateBatchSuccess,
    submitAction,
    submitValidateBatchAction,
    title,
    subTitle,
    type,
    itemsToLoadAfterValidating,
    showItemToLoad,
    onCancelAfterValidate,
    onChangeFile,
    onSubmitWithWarehouse,
}) => {
    const { t } = useTranslation(["home", "devices"]);

    const [showWarehouseModal, setShowWarehouseModal] = useState(false);
    const [pendingFile, setPendingFile] = useState(null);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [loadingValidate, setLoadingValidate] = useState(false);
    const [identifiers, setIdentifiers] = useState([]);

    const buildFormData = (file) => {
        const data = new FormData();
        data.append("file", file);
        data.append("tz", new Date().getTimezoneOffset());
        return data;
    };

    useEffect(() => {
        if (submitValidateBatchSuccess && itemsToLoadAfterValidating) {
            
            let availableItems = [];
            let ids = [];
            
            if (itemsToLoadAfterValidating.props && itemsToLoadAfterValidating.props.itemsAvailableToInsert) {
                availableItems = itemsToLoadAfterValidating.props.itemsAvailableToInsert;
            } 
            else if (itemsToLoadAfterValidating.validations && itemsToLoadAfterValidating.validations.available_to_insert) {
                availableItems = itemsToLoadAfterValidating.validations.available_to_insert;
            } 
            else if (itemsToLoadAfterValidating.props && itemsToLoadAfterValidating.props.validations && 
                     itemsToLoadAfterValidating.props.validations.available_to_insert) {
                availableItems = itemsToLoadAfterValidating.props.validations.available_to_insert;
            }
            else if (Array.isArray(itemsToLoadAfterValidating)) {
                availableItems = itemsToLoadAfterValidating;
            }
            
            if (availableItems && availableItems.length > 0) {
                if (type === 'simcards') {
                    ids = availableItems.map(item => item.iccid);
                } else if (type === 'devices') {
                    ids = availableItems.map(item => item.imei);
                }

                if (ids.length > 0) {
                    setIdentifiers(ids);
                }
            }
        }
    }, [submitValidateBatchSuccess, itemsToLoadAfterValidating, type]);

    const handleOnSubmit = async (file) => {
        setLoadingSubmit(true);
        try {
            const data = buildFormData(file);

            const options = { 
                suppressSuccessMessage: true,
                skipCleanValidatedDevices: true,  
                skipGlobalError: true,
                skipReset: true 
            };
            
            const result = await submitAction(data, options);
            
            setPendingFile(file);
            setShowWarehouseModal(true);
        } finally {
            setLoadingSubmit(false);
        }
    };


    const handleOnSubmitValidateFile = async (file) => {
        setLoadingValidate(true);
        setPendingFile(file);
        try {
            const data = buildFormData(file);
            await submitValidateBatchAction(data);
        } finally {
            setLoadingValidate(false);
        }
    };

    const handleWarehouseContinue = async (warehouse) => {
        if (identifiers.length > 0 && warehouse) {
            try {
                let response;

                const options = {
                    executeCleanValidatedDevices: true,  
                    showSuccessMessage: true          
                };
                
                if (type === 'simcards') {
                    response = await onSubmitWithWarehouse({
                        iccids: identifiers,
                        data: {
                            status: 'bodega',
                            substatus: {
                                referenceModel: 'Warehouse',
                                referenceId: warehouse._id,
                            },
                        },
                    }, options);
                } else if (type === 'devices') {
                    response = await onSubmitWithWarehouse({
                        imei: identifiers,
                        data: {
                            status: 'bodega',
                            substatus: {
                                referenceModel: 'Warehouse',
                                referenceId: warehouse._id,
                            },
                        },
                    }, options);
                }
                

                if (response && response.success) {
                    const successMessage = type === 'devices' ? 
                        `Dispositivos cargados y asignados a bodega exitosamente` : 
                        `Simcards cargadas y asignadas a bodega exitosamente`;
                    
                    if (window.toast && window.toast.success) {
                        window.toast.success(successMessage);
                    } else if (window.alert) {
                        window.alert(successMessage);
                    }

                    setShowWarehouseModal(false);

                    setPendingFile(null);

                    if (typeof onClose === 'function') {
                        onClose();
                    }

                    if (window.cleanValidatedDevices && typeof window.cleanValidatedDevices === 'function') {
                        window.cleanValidatedDevices();
                    }
                }
            } catch (error) {
                console.error('Error al asignar bodega:', error);

            }
        }
    };


    const handleWarehouseClose = (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }
   
        setShowWarehouseModal(false);
        
        setPendingFile(null);
        if (typeof onClose === 'function') {
            onClose();
        }

        // if (window.cleanValidatedDevices && typeof window.cleanValidatedDevices === 'function') {
        //     window.cleanValidatedDevices();
        // }
    };

    return (
        <>
            <CustomDialogUploadFile
                open={open && !showWarehouseModal}
                onClose={onClose}
                title={title}
                subTitle={subTitle}
                onSubmit={handleOnSubmit}
                onSubmitValidateFile={handleOnSubmitValidateFile}
                typesFile={"xslx"}
                submitFetchingFile={loadingSubmit}
                submitValidateFetchingFile={loadingValidate}
                validateFileBeforeUpload={true}
                submitValidateBatchSuccess={submitValidateBatchSuccess}
                itemsToLoadAfterValidating={itemsToLoadAfterValidating}
                showItemToLoad={showItemToLoad}
                onChangeFile={onChangeFile}
                onCancelAfterValidate={onCancelAfterValidate}
                downloadExample={
                    <div className={styles.link}>
                        <a
                            href={`${api}/examples/${type}.xlsx`}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                        >
                            <SVG src={downloadSVG} />
                            {t("devices:download_example_file")}
                        </a>
                    </div>
                }
            />
            <WarehouseAfterBatchModal
                open={showWarehouseModal}
                onClose={handleWarehouseClose}
                onSubmit={handleWarehouseContinue}
                type={type}
                identifiers={identifiers}
            />
        </>
    );
};

export default memo(CreateByBatch);
